import { useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useTaskStore } from '@/store/useTaskStore'
import { api } from '@/lib/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatTime } from '@/lib/utils'
import type { LogEntry as LogEntryType } from '@/lib/types'

export function LogViewer() {
  const { activeRunId, logs, addLog, clearLogs } = useTaskStore()
  const eventSourceRef = useRef<EventSource | null>(null)
  const logContainerRef = useRef<HTMLDivElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)

  useEffect(() => {
    // Close any existing EventSource connection first
    if (eventSourceRef.current) {
      eventSourceRef.current.close()
      eventSourceRef.current = null
    }

    if (!activeRunId) {
      clearLogs()
      return
    }

    api.getRunStatus(activeRunId).then((data) => {
      clearLogs()
      data.log.slice(-40).reverse().forEach(addLog)
    }).catch((err) => {
      console.error('Failed to get run status:', err)
    })

    const eventSource = new EventSource(api.getStreamUrl(activeRunId))
    eventSourceRef.current = eventSource

    eventSource.onmessage = (event) => {
      try {
        const entry: LogEntryType = JSON.parse(event.data)
        addLog(entry)

        if (logContainerRef.current) {
          logContainerRef.current.scrollTop = 0
        }
      } catch (err) {
        console.error('Failed to parse log entry:', err)
      }
    }

    eventSource.onerror = (err) => {
      console.error('EventSource error:', err)
      // Close the connection on error to prevent infinite reconnection attempts
      eventSource.close()
    }

    return () => {
      eventSource.close()
      eventSourceRef.current = null
    }
  }, [activeRunId, addLog, clearLogs])

  if (!activeRunId) {
    return (
      <Card>
        <CardHeader
          className="cursor-pointer select-none"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-slate-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-slate-400" />
            )}
            <CardTitle>Process Log</CardTitle>
          </div>
        </CardHeader>
        {isExpanded && (
          <CardContent>
            <div className="h-64 flex items-center justify-center bg-slate-950 rounded-md border border-slate-800">
              <p className="text-sm text-slate-500">
                Start a task to see live logs
              </p>
            </div>
          </CardContent>
        )}
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader
        className="cursor-pointer select-none hover:bg-slate-900"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronRight className="h-4 w-4 text-slate-400" />
          )}
          <CardTitle>Process Log</CardTitle>
          <span className="ml-auto text-xs text-slate-500">
            {logs.length} events
          </span>
        </div>
      </CardHeader>
      {isExpanded && (
        <CardContent>
          <div
            ref={logContainerRef}
            className="h-72 overflow-auto bg-slate-950 rounded-md border border-slate-800 p-3 font-mono text-xs space-y-1.5"
          >
            {logs.length === 0 ? (
              <p className="text-slate-500">Waiting for logs...</p>
            ) : (
              logs.map((log, idx) => <LogEntry key={idx} entry={log} />)
            )}
          </div>
        </CardContent>
      )}
    </Card>
  )
}

function LogEntry({ entry }: { entry: LogEntryType }) {
  const isError = entry.event?.toLowerCase().includes('error')
  const eventColor = isError ? 'text-red-400' : 'text-amber-400'

  return (
    <div className="flex gap-3 text-xs">
      <span className="text-blue-400">[{formatTime(entry.timestamp)}]</span>
      <span className={`uppercase ${eventColor} min-w-[112px]`}>
        {entry.event}
      </span>
      <span className={isError ? 'text-red-300' : 'text-green-300'}>
        {entry.message || entry.label || ''}
      </span>
    </div>
  )
}
